import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { setSentryUser } from '@/lib/sentry';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) { setUser(null); setSentryUser(null); setLoading(false); return; }
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      setSentryUser(res.data);
    } catch {
      localStorage.removeItem('session_token');
      setUser(null);
      setSentryUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password) => {
    const res = await api.post('/api/auth/login', {
      identifier,
      password,
    });
    const token = res.data.token || res.data.session_id;
    if (token) localStorage.setItem('session_token', token);
    // Reset player's last-viewed tab so a fresh login always lands on the
    // main lobby (home), not the tab they happened to be on before logout.
    try { localStorage.removeItem('player_active_tab'); } catch {}
    setUser(res.data.user);
    // Iter 142.6 — pin the player to all subsequent Sentry events
    setSentryUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, fullName, username = null, referralCode = null, tenant = null, referralSource = null, emailOtp = null, options = {}) => {
    const payload = {
      password,
      username: username || undefined,
      referral_code: referralCode || undefined,
      referral_source: referralSource || undefined,
      email_otp: emailOtp || undefined,
      tenant_slug: tenant || undefined,
      account_type: options.accountType || undefined,
    };
    if (email) payload.email = email;
    const res = await api.post('/api/auth/signup', payload);
    const token = res.data.token || res.data.session_id;
    if (token) localStorage.setItem('session_token', token);
    try { localStorage.removeItem('player_active_tab'); } catch {}
    setUser(res.data.user);
    setSentryUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/api/auth/logout'); } catch {}
    localStorage.removeItem('session_token');
    // Drop the tab memory on logout too, so if another user logs in from
    // the same browser they get the clean landing page.
    try { localStorage.removeItem('player_active_tab'); } catch {}
    setUser(null);
    // Iter 142.6 — clear Sentry user so post-logout errors aren't
    // attributed to the old player.
    setSentryUser(null);
  };

  // Iter 142.8: hydrate the AuthContext from outside (used by the
  // Google OAuth callback page after it exchanges the code for a session).
  // Without this, the next route render would do an unnecessary /me round
  // trip just to learn what we already know.
  const setUserDirect = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, setUserDirect,
      checkAuth, tenantSlug: null, setTenantSlug: () => {}
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
